/**
 * T-09 · El armazón del panel. Rehecho en `T-37` sobre el prototipo del Armador.
 *
 * Decide **una sola cosa**: si hay sesión, se ve el panel; si no, la pantalla de
 * entrada. Todo lo demás cuelga de ahí.
 *
 * ── La protección de verdad NO está aquí ────────────────────────────────────
 * Está en el servidor: el interceptor responde 401 sin cookie válida y 403 sin
 * permiso, y eso no se puede rodear desde el navegador. Este archivo sólo evita
 * **enseñar** lo que no se puede usar.
 *
 * Confundir las dos cosas es cómo se acaba con un panel que oculta un botón y
 * una API que deja llamarlo.
 *
 * ── T-37 · Por qué volvió a cambiar la forma ────────────────────────────────
 * La versión de `T-36` salió del handoff anterior y se quedó corta: una lateral
 * de 236 px y una barra superior que sólo llevaba el título. Isaac lo dijo dos
 * veces —«quiero que la interfaz sea la misma»— y tenía razón: el prototipo
 * (`apps/web/diseno/Armador de Menu.dc.html`) define **tres franjas** donde
 * había una, y una lateral con tarjeta de estado abajo.
 *
 *   1. **Banda de encabezado**: «Armador de menú» y el estado del guardado.
 *   2. El contenido.
 *
 * Eran tres. La primera —«Barra de cuenta»: sucursal, «Escanear QR» y salir—
 * **se quitó el 2026-09-26**: el selector de sucursal nunca se pintó porque no
 * había qué listar, «Escanear QR» llevaba a una fase que no existe, y salir se
 * mudó al lateral, que es donde está todo lo que no es una sección. Una franja
 * de 58 px con un solo botón deshabilitado le quitaba sitio al contenido y no
 * daba nada.
 *
 * Las medidas viven en `panel.css`, sacadas de los estilos en línea del
 * prototipo.
 *
 * ── Lo que el diseño dice y todavía NO puede decir la verdad ────────────────
 * El prototipo es una maqueta con datos inventados —«Café Aurora», «Sucursal
 * Centro», «Lucía Medina»— y `GET /yo` devuelve `identidad { staffUserId,
 * tenantId }`, `permisos` y `modulos`: **ni un nombre**. Verificado contra la
 * API viva, no de memoria; ya me equivoqué una vez esta semana afirmando lo
 * contrario.
 *
 * Isaac eligió «barra sin nombres por ahora», así que la barra se construye y
 * los huecos se quedan **vacíos**, no rellenos con un nombre inventado. Lo que
 * no tiene de dónde salir tampoco se finge:
 *
 *  · **El selector de sucursal no se pinta.** Un desplegable que no puede
 *    listar nada es peor que su ausencia: promete elegir y no deja.
 *  · **La cuenta enseña «Mi cuenta» y el botón de salir**, que es lo cierto que
 *    hay hoy en ese sitio. El nombre y el cargo llegan con el endpoint.
 *  · **«Previsualizar menú» no se pinta**: lleva al menú público, que es
 *    `menu.fialrest.com/<slug>` (`ADR-004`), y el panel **no tiene el slug**.
 *    Un enlace con el `tenantId` daría un 404.
 *  · **«Publicar cambios» no se pinta.** En este producto se publica **platillo
 *    por platillo** —`draft | published` vive en cada platillo— y no hay
 *    borrador global que publicar. Un botón que lo prometiera sería de las
 *    peores mentiras posibles: la que hace creer a alguien que su carta ya está
 *    en la mesa. La tarjeta de estado sí se queda, **diciendo lo que es cierto**
 *    —que el menú está publicado— sin el botón.
 *  · **«Escanear QR» ya no se pinta.** Se mantuvo un tiempo deshabilitado con
 *    su motivo —que es distinto de mentir— y Isaac lo retiró el 2026-09-26: el
 *    escaneo vive en el mostrador, que tiene su propia aplicación, y un botón
 *    que nunca se va a encender aquí es un hueco permanente.
 */
import type { ReactNode } from 'react'
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { Boton, Aviso } from '@fialrest/ui'
import { puede, useYo, useSalir, type Yo } from '../api/sesion.js'
import type { Modulo } from '@fialrest/shared'
import { ErrorDeApi } from '@fialrest/cliente'
import './panel.css'

/**
 * Las secciones del panel. El módulo que cada una necesita, si necesita alguno.
 *
 * > [!info] Una sección **no** es un módulo — `T-37`
 * > «Fotografías» y «Menú» declaran las dos `menu`, y no por parecido: las
 * > diecisiete rutas de `menu.controller.ts` llevan `@RequiereModulo('menu')`
 * > **incluidas las de lectura**, así que las dos pantallas dejan de funcionar a
 * > la vez. Lo que se vende es el módulo; lo que se navega son secciones, y una
 * > cosa puede tener varias de la otra. El orden es el del prototipo: el armador
 * > primero, Fotografías detrás.
 */
export const SECCIONES = [
  // `texto` es el rótulo de la lateral; `encabezado` el de la banda, que el
  // prototipo escribe distinto a propósito — la lateral dice dónde estás en una
  // palabra y el encabezado nombra la herramienta. `pie` es la línea de debajo.
  {
    a: '/menu', texto: 'Menú', modulo: 'menu' as const, permiso: undefined, icono: 'menu' as const,
    encabezado: 'Armador de menú',
    pie: 'Guardado automático · cada cambio sale a la mesa al momento',
  },
  {
    a: '/fotografias', texto: 'Fotografías', modulo: 'menu' as const, permiso: undefined, icono: 'foto' as const,
    encabezado: 'Fotografías',
    pie: 'El logotipo, la portada y la foto de cada platillo',
  },
  {
    // `T-41` · Va tras Fotografías y antes de Lealtad: pertenece al módulo del
    // menú, como las dos de arriba, y se usa mucho menos que ellas — se arma una
    // vez y se toca cuando sube un proveedor.
    a: '/extras', texto: 'Extras', modulo: 'menu' as const, permiso: undefined, icono: 'extra' as const,
    encabezado: 'Extras y agregados',
    pie: 'El catálogo de la cafetería · el precio vive aquí y vale para todos',
    // `T-42` · Esta pantalla trae su propia cabecera, la del estándar de
    // estilos: pastilla, título de 30 px y una línea con tope de 640. La banda
    // se calla en ella porque si no el título saldría **dos veces**, que es
    // exactamente lo que pasó al montarlo la primera vez.
    //
    // Es una salida, no la norma: las otras cuatro secciones siguen con la
    // banda de siempre hasta que Isaac vea ésta y decida si el estándar se
    // extiende. Probar una forma nueva en una pantalla cuesta esta bandera;
    // extenderla a cinco antes de mirarla cuesta cinco pantallas.
    cabeceraPropia: true,
  },
  {
    // `T-43` · Detrás de Extras y por lo mismo: pertenece al módulo del menú y
    // se toca todavía menos —una plantilla se define una vez y dura años—. Va
    // después porque los extras se usan a diario y esto no.
    a: '/tamanos', texto: 'Tamaños', modulo: 'menu' as const, permiso: undefined, icono: 'tamano' as const,
    encabezado: 'Tamaños',
    pie: 'Las plantillas de la casa · se aplican a un producto y no lo atan',
    cabeceraPropia: true,
  },
  {
    //  `T-67` · Cabecera propia, como Menú, Extras y Tamaños: el título, las
    //  cuatro pestañas y el selector de sucursal son **una sola pieza** —la
    //  línea de abajo los cose— y partirla en dos dejaría el selector flotando
    //  sobre el contenido.
    a: '/lealtad', texto: 'Lealtad', modulo: 'loyalty' as const, permiso: undefined, icono: 'estrella' as const,
    encabezado: 'Lealtad', pie: 'El programa de sellos y lo que pasa con él',
    cabeceraPropia: true,
  },
  {
    //  `T-65` · La primera sección que se enseña **por permiso** y no por módulo.
    //  El personal no pertenece a nada que se venda por separado —una cafetería
    //  que sólo contrató el menú tiene gente que lo edita—, pero sí a una
    //  llave: sin `staff.manage` las siete rutas contestan 403, y hasta hoy la
    //  entrada se le pintaba igual a una cajera.
    a: '/personal', texto: 'Personal', modulo: undefined,
    permiso: 'staff.manage' as const, icono: 'persona' as const,
    encabezado: 'Personal',
    pie: 'Quién trabaja aquí y con qué llaves. Una baja no borra su historial.',
  },
  {
    //  `T-65` · Hermana de Personal y sección aparte, por decisión de Isaac del
    //  2026-09-28. Son dos permisos distintos: dejar que una encargada revoque
    //  la tablet robada de su turno no es dejarle dar de alta personal.
    //
    //  Va justo detrás porque se usa mucho menos —una tablet se revoca el día
    //  que se pierde— y porque quien busca una busca la otra.
    a: '/aparatos', texto: 'Aparatos', modulo: undefined,
    permiso: 'device.revoke' as const, icono: 'tablet' as const,
    encabezado: 'Aparatos',
    pie: 'Las tablets del mostrador. Se dan de alta solas; aquí se revocan.',
  },
  {
    //  `T-49` · La primera sección que NO pertenece a un módulo y que además
    //  tiene pantalla — `ADR-029`: «se ve siempre, con menú o sin él». Una
    //  cafetería que sólo contrató lealtad tiene nombre, dirección y horario
    //  igual que cualquier otra.
    //
    //  Va la última porque se toca poco: se llena una vez al abrir y se vuelve
    //  cuando cambia un horario de verano.
    a: '/mi-cafeteria', texto: 'Mi cafetería', modulo: undefined, permiso: undefined,
    icono: 'tienda' as const,
    encabezado: 'Mi cafetería',
    pie: 'La ficha de tu negocio. La ve todo tu equipo; la cambia quien tiene permiso.',
  },
] as const

/**
 * Los iconos de la navegación, copiados del prototipo trazo por trazo: los
 * cuatro son `viewBox="0 0 16 16"`, sin relleno y con el trazo del color del
 * texto, para que el activo cambie de color con su enlace y no haya que pintar
 * dos versiones de cada uno.
 *
 * `aria-hidden` en todos: el rótulo va al lado y un lector que dijera «imagen,
 * Menú» sólo repetiría.
 *
 * El prototipo trae además «Inicio» y «Marca y Colores». **No se pintan**: no
 * existe ninguna de las dos pantallas, y una puerta sin habitación detrás es lo
 * que este archivo lleva evitando desde `T-36`. Entran el día que existan.
 */
const ICONOS = {
  menu: <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />,
  foto: <><rect x="2.3" y="3.3" width="11.4" height="9.4" rx="2" /><path d="M2.6 11l3.4-2.8 2.6 2 2-1.7 3.1 2.6" /></>,
  estrella: <path d="M8 2.2l1.8 3.7 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4L2.2 6.5l4-.6z" />,
  persona: <><circle cx="8" cy="5.8" r="2.6" /><path d="M3 13.4c.7-2.3 2.6-3.5 5-3.5s4.3 1.2 5 3.5" /></>,
  //  `T-49` · El toldo de un local, que es como se dibuja «mi negocio» en la
  //  calle. No una casa —eso es «inicio»— ni un engranaje: `ADR-029` rechazó
  //  llamar a esto «Configuración», y el icono tiene que decir lo mismo que el
  //  rótulo o el rótulo deja de mandar.
  tienda: <><path d="M2.4 6.2h11.2M2.4 6.2l1.1-3h9l1.1 3" /><path d="M3.3 6.2v7.1h9.4V6.2" /><path d="M6.4 13.3V9.4h3.2v3.9" /></>,
  // `T-41` · Un chip con un «+»: es como se pintan los extras en el editor, así
  // que el icono de la lateral y lo que se encuentra al entrar dicen lo mismo.
  extra: <><rect x="1.8" y="5.2" width="12.4" height="5.6" rx="2.8" /><path d="M8 7.1v1.8M7.1 8h1.8" /></>,
  // `T-43` · Tres barras de altura creciente: «lo mismo, en varios tamaños».
  // No un vaso —el icono obvio— porque las plantillas no son sólo de bebida: un
  // pan también tiene chico y grande.
  tamano: <><path d="M3 12.6V9.4M8 12.6V6.2M13 12.6V3.8" /></>,
  //  `T-65` · Una tablet: rectángulo vertical con el botón abajo. No un
  //  candado —eso diría «bloqueado», y aquí lo normal es que estén trabajando—
  //  ni un engranaje, que no dice de qué son los ajustes.
  tablet: <><rect x="4.2" y="1.8" width="7.6" height="12.4" rx="1.6" /><path d="M7 12.6h2" /></>,
  //  Una puerta con la flecha saliendo, que es como se dibuja «salir» en todas
  //  partes. No una `x` —eso es cerrar una cosa, no irse— ni un candado, que
  //  dice «bloqueado» y aquí nadie bloquea nada.
  salir: <><path d="M9.6 3.4H4.2a1 1 0 0 0-1 1v7.2a1 1 0 0 0 1 1h5.4" /><path d="M11.2 5.8 13.4 8l-2.2 2.2M13.4 8H6.6" /></>,
} satisfies Record<string, ReactNode>

function Icono({ nombre }: { readonly nombre: keyof typeof ICONOS }): ReactNode {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.4"
      strokeLinecap="round" strokeLinejoin="round"
    >
      {ICONOS[nombre]}
    </svg>
  )
}

/**
 * Qué secciones se muestran.
 *
 * `closed` no se muestra; `suspended` **sí** —el dueño sigue viendo lo que pagó,
 * en sólo lectura— y sin contratar tampoco. Es `ADR-011` aplicado a la
 * navegación, y sale del estado que manda `GET /yo`, no de una lista local.
 */
/**
 * `T-65` · Las **dos puertas** de una sección, y ninguna sustituye a la otra: el
 * módulo dice *qué se vendió* y el permiso *quién tiene la llave*. Una sección
 * puede depender de las dos, de una, o de ninguna.
 *
 * El permiso es el que faltaba: hasta hoy `if (!s.modulo) return true` le
 * pintaba «Personal» a una cajera, que al entrar recibía 403 en cada llamada.
 *
 * ── Por qué está suelta y no dentro del `filter` ──────────────────────────
 * Porque si no, **la mitad de su comportamiento no se puede probar**. Hoy
 * ninguna sección real declara módulo *y* permiso a la vez, así que colapsar las
 * dos puertas en una —`if (s.permiso) return puede(yo, s.permiso)`— pasa la
 * suite entera en verde. Se descubrió saboteándolo el 2026-09-28: el sabotaje no
 * disparó, y un sabotaje que no dispara dice que la propiedad no es observable,
 * no que esté bien.
 *
 * Suelta, se le puede dar una sección con las dos y comprobar que exige las dos
 * — que es lo que va a hacer falta el día que una sección de módulo pida además
 * su casilla.
 */
export function seVeLaSeccion(
  s: { readonly modulo?: Modulo | undefined, readonly permiso?: string | undefined },
  yo: Yo | undefined,
): boolean {
  if (s.permiso !== undefined && !puede(yo, s.permiso)) return false
  if (s.modulo === undefined) return true
  const estado = yo?.modulos[s.modulo]
  return estado === 'active' || estado === 'suspended'
}

export function seccionesVisibles(yo: Yo | undefined): typeof SECCIONES[number][] {
  return SECCIONES.filter((s) => seVeLaSeccion(s, yo))
}

/**
 * En qué sección estamos, para el título de la barra superior.
 *
 * Se compara con el prefijo y no sólo con la ruta exacta porque una sección
 * puede ganar rutas hijas (`/menu/categorias/:id`) sin que el título se quede
 * en blanco. Y **no mira `seccionesVisibles`**: el título dice dónde estás, no
 * qué puedes; quien decide qué se pinta dentro es `SeccionDeModulo`.
 */
export function seccionDeLaRuta(ruta: string): typeof SECCIONES[number] | undefined {
  return SECCIONES.find((s) => ruta === s.a || ruta.startsWith(`${s.a}/`))
}

export function Armazon(): ReactNode {
  const { data: yo, isPending, error } = useYo()
  const salir = useSalir()
  const navegar = useNavigate()
  const { pathname } = useLocation()

  if (isPending) {
    // Un texto y no un esqueleto animado: el panel carga en milisegundos, y una
    // animación que aparece y desaparece se lee como un parpadeo.
    return <p style={{ padding: 'var(--e5)', color: 'var(--neutro-600)' }}>Cargando…</p>
  }

  if (error) {
    if (error instanceof ErrorDeApi && error.esSinSesion) {
      // Sin sesión no es un error que mostrar: es ir a entrar.
      //
      // Se DEVUELVE `<Navigate>` en vez de llamar a `navegar()` aquí: llamarlo
      // durante el render es un efecto secundario en mitad de una función que
      // debe ser pura — React avisa, y con el render doble de StrictMode puede
      // navegar dos veces. Lo cazó el linter al marcar la promesa suelta.
      return <Navigate to="/entrar" replace />
    }
    return (
      <div style={{ padding: 'var(--e5)', display: 'grid', gap: 'var(--e4)', maxWidth: 480 }}>
        <Aviso>{error instanceof Error ? error.message : 'No se pudo cargar el panel.'}</Aviso>
        <div><Boton variante="principal" onClick={() => location.reload()}>Reintentar</Boton></div>
      </div>
    )
  }

  const visibles = seccionesVisibles(yo)
  const actual = seccionDeLaRuta(pathname)

  return (
    <div className="panel">
      {/* La barra lateral del diseño. Por debajo del punto de corte es una banda
          horizontal — ver la cabecera de `panel.css`. */}
      <div className="panel__lateral">
        <div className="panel__marca">
          {/* La inicial es decorativa: el nombre está justo al lado, y un lector
              de pantalla que leyera «F FialRest» sólo repetiría. */}
          <div className="panel__placa" aria-hidden="true">F</div>
          <div style={{ minWidth: 0 }}>
            {/* Donde el prototipo pone «Café Aurora» va la marca del producto,
                que sí es cierta, hasta que `GET /yo` traiga el nombre del
                negocio. Inventarlo sería enseñarle a un dueño el nombre de otra
                cafetería. */}
            <div className="panel__nombre">FialRest</div>
            <div className="panel__rotulo">Panel del dueño</div>
          </div>
        </div>

        <nav className="panel__nav" aria-label="Secciones">
          {visibles.map((s) => (
            // `NavLink` pone `aria-current="page"` solo, que es lo que la
            // navegación de T-08 usa para anunciar dónde estás — y de lo que
            // cuelga el estilo del elemento activo en `panel.css`.
            <NavLink key={s.a} to={s.a} className="panel__enlace">
              <Icono nombre={s.icono} />
              {s.texto}
            </NavLink>
          ))}
        </nav>

        {/* La tarjeta de estado del prototipo, SIN su botón «Publicar cambios»:
            se publica platillo por platillo, así que no hay nada que publicar de
            golpe. Lo que queda es cierto — el menú está en la mesa — y por eso
            se queda. Ver la cabecera. */}
        <div className="panel__estado">
          <div className="panel__estado-linea">
            <span className="panel__estado-punto" aria-hidden="true" />
            <span className="panel__estado-titulo">Menú publicado</span>
          </div>
          <p className="panel__estado-detalle">
            Cada platillo se publica por su cuenta: lo que está visible ya está
            en la mesa.
          </p>
        </div>

        {/*  Salir vive aquí desde el 2026-09-26, por decisión de Isaac: la barra
            superior tenía dos botones y ninguno se usaba —«Escanear QR» lleva a
            una fase que no existe y salir estaba lejos de todo lo demás—, así
            que la franja entera se quitó.

            Va **fuera** de la tarjeta de estado y no dentro: esa tarjeta no se
            pinta en móvil —ver `panel.css`— y salir tiene que poder hacerse
            desde un teléfono. Es lo último del lateral porque es lo último que
            alguien hace. */}
        <button
          type="button"
          className="panel__salir"
          onClick={() => {
            salir.mutate(undefined, {
              onSettled: () => { void navegar('/entrar', { replace: true }) },
            })
          }}
          disabled={salir.isPending}
        >
          <Icono nombre="salir" />
          {salir.isPending ? 'Saliendo…' : 'Cerrar sesión'}
        </button>
      </div>

      <div className="panel__cuerpo">
        {/* ── 1 · La banda de encabezado de la pantalla ───────────────────── */}
        {/* `in` y no un campo opcional en las cinco: `SECCIONES` es `as const`
            y ponerle `cabeceraPropia: false` a las otras cuatro sería escribir
            cuatro veces «esta no» para que una diga «esta sí». */}
        {actual !== undefined && 'cabeceraPropia' in actual ? null : (
          <div className="panel__encabezado">
            <div>
              <h1 className="panel__titulo">{actual?.encabezado ?? 'Panel'}</h1>
              {actual?.pie ? <p className="panel__subtitulo">{actual.pie}</p> : null}
            </div>
          </div>
        )}

        {/* ── 3 · El contenido ────────────────────────────────────────────── */}
        <main className="panel__contenido">
          <Outlet context={yo} />
        </main>
      </div>
    </div>
  )
}
